/**
 * Smart Onboarding Chat Panel
 *
 * AI assistant chat with:
 * - Word-by-word typing animation
 * - Contextual loading messages
 * - Suggested follow-up questions
 * - Proactive tip cards
 * - Action buttons
 * - Guided setup for new users
 * - Mobile-first UX
 */

import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import { X, Send, ArrowRight, Trash2, AlertTriangle, Lightbulb, ChevronDown, LifeBuoy, Sparkles, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';
import type { SmartAssistantMessage, AssistantAction, ProactiveTip, AssistantMode } from '@/types/assistant';

// ─── Typing Animation Hook ───

function useTypingAnimation(content: string, isActive: boolean) {
  const [displayedContent, setDisplayedContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const wordsRef = useRef<string[]>([]);
  const indexRef = useRef(0);
  const rafRef = useRef<number>(0);
  const lastTimeRef = useRef(0);

  useEffect(() => {
    if (!isActive || !content) {
      setDisplayedContent(content);
      setIsTyping(false);
      return;
    }

    // Split into words, keeping whitespace/newlines
    wordsRef.current = content.match(/\S+|\s+/g) || [content];
    indexRef.current = 0;
    setDisplayedContent('');
    setIsTyping(true);
    lastTimeRef.current = 0;

    const WORD_DELAY = 18; // ms per word

    function step(timestamp: number) {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const elapsed = timestamp - lastTimeRef.current;

      if (elapsed >= WORD_DELAY) {
        lastTimeRef.current = timestamp;
        indexRef.current++;

        if (indexRef.current >= wordsRef.current.length) {
          setDisplayedContent(content);
          setIsTyping(false);
          return;
        }

        setDisplayedContent(wordsRef.current.slice(0, indexRef.current + 1).join(''));
      }

      if (indexRef.current < wordsRef.current.length) {
        rafRef.current = requestAnimationFrame(step);
      }
    }

    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [content, isActive]);

  // Skip to end
  const skipToEnd = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setDisplayedContent(content);
    setIsTyping(false);
  }, [content]);

  return { displayedContent, isTyping, skipToEnd };
}

// ─── Contextual Loading Messages ───

const LOADING_MESSAGES_HR = [
  'Razmišljam...',
  'Provjeravam tvoje podatke...',
  'Gledam što ti treba...',
  'Pripremam odgovor...',
];

const LOADING_MESSAGES_EN = [
  'Thinking...',
  'Checking your data...',
  'Looking into it...',
  'Preparing answer...',
];

function useLoadingMessage(isLoading: boolean, language: string) {
  const [messageIndex, setMessageIndex] = useState(0);
  const messages = language === 'hr' ? LOADING_MESSAGES_HR : LOADING_MESSAGES_EN;

  useEffect(() => {
    if (!isLoading) {
      setMessageIndex(0);
      return;
    }

    // Rotate messages every 2.5 seconds
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % messages.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [isLoading, messages.length]);

  return messages[messageIndex];
}

// ─── Main Component ───

interface OnboardingChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  messages: SmartAssistantMessage[];
  isLoading: boolean;
  onSendMessage: (message: string) => Promise<void>;
  onExecuteAction: (action: AssistantAction) => void;
  quickSuggestions: { icon: string; label: string; query: string }[];
  welcomeMessage: string;
  onClear?: () => void;
  guidedMode?: boolean;
  onDismissGuide?: () => void;
  mode?: AssistantMode;
  onSwitchMode?: () => void;
  onUploadScreenshot?: (file: File) => void;
}

export function OnboardingChatPanel({
  isOpen,
  onClose,
  messages,
  isLoading,
  onSendMessage,
  onExecuteAction,
  quickSuggestions,
  welcomeMessage,
  onClear,
  guidedMode,
  onDismissGuide,
  mode = 'assistant',
  onSwitchMode,
  onUploadScreenshot,
}: OnboardingChatPanelProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.substring(0, 2) || 'hr';

  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const loadingMessage = useLoadingMessage(isLoading, language);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;
    const message = inputValue.trim();
    setInputValue('');
    await onSendMessage(message);
  };

  const handleQuickSuggestion = async (query: string) => {
    if (isLoading) return;
    await onSendMessage(query);
  };

  // Filter visible messages (hide setup check user messages)
  const visibleMessages = messages.filter(m => !(m.type === 'user' && m.content === '__setup_check__'));
  const showWelcome = visibleMessages.filter(m => m.type === 'user').length === 0;

  return (
    <div
      className={cn(
        'fixed z-50 flex flex-col',
        'bg-white dark:bg-gray-950',
        'border border-gray-200/80 dark:border-gray-800',
        'shadow-2xl shadow-blue-500/10 dark:shadow-black/40',
        'backdrop-blur-xl',
        'transition-all duration-300 ease-out',
        // Mobile: full-screen panel (close via the X in the header)
        'inset-0 w-full h-[100dvh] max-h-[100dvh]',
        // Desktop: floating panel above the FAB button
        'md:inset-auto md:bottom-[5.5rem] md:right-6 md:left-auto md:top-auto',
        'md:h-[640px] md:w-[440px] md:rounded-3xl',
        isOpen
          ? 'translate-y-0 opacity-100 scale-100'
          : 'translate-y-full md:translate-y-4 opacity-0 scale-95 pointer-events-none'
      )}
    >
      {/* Header — compact, modern, with status dot */}
      <div className={cn(
        'relative flex items-center justify-between px-4 py-3 shrink-0',
        'overflow-hidden md:rounded-t-3xl',
        // Respect the notch / status bar when full-screen on mobile
        'pt-[calc(0.75rem+env(safe-area-inset-top))] md:pt-3',
        mode === 'support'
          ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600'
          : 'bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-600'
      )}>
        {/* Decorative blur orb */}
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-8 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none" />

        <div className="relative flex items-center gap-3">
          {/* Avatar with animated gradient ring */}
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-cyan-300 via-white/50 to-blue-300 opacity-80 blur-[2px]" />
            {mode === 'support' ? (
              <div className="relative w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/40">
                <LifeBuoy className="w-5 h-5 text-white" />
              </div>
            ) : (
              <img src="/ai-bubble.png" alt="AI" className="relative w-10 h-10 rounded-full object-cover ring-2 ring-white/60" />
            )}
            {/* Online status dot */}
            <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white dark:ring-blue-700">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
            </span>
          </div>
          <div className="relative">
            <h3 className="text-white font-semibold text-sm leading-tight">
              {mode === 'support'
                ? t('onboarding.chatPanel.techSupport', 'Technical Support')
                : t('onboarding.chatPanel.aiAssistant', 'AI Assistant')}
            </h3>
            <p className="text-white/70 text-[11px] leading-tight mt-0.5">
              {mode === 'support'
                ? t('onboarding.chatPanel.reportIssue', 'Report issue or ask')
                : t('onboarding.chatPanel.fellowHost', 'Your fellow host')}
            </p>
          </div>
        </div>
        <div className="relative flex items-center gap-0.5">
          {onSwitchMode && (
            <button
              onClick={onSwitchMode}
              className="p-2 text-white/80 hover:text-white hover:bg-white/15 rounded-xl transition-all"
              title={mode === 'support'
                ? t('onboarding.chatPanel.switchToAssistant', 'Switch to assistant')
                : t('onboarding.chatPanel.switchToSupport', 'Switch to support')}
            >
              {mode === 'support' ? <Sparkles className="w-4 h-4" /> : <LifeBuoy className="w-4 h-4" />}
            </button>
          )}
          {onClear && messages.length > 0 && (
            <button
              onClick={onClear}
              className="p-2 text-white/80 hover:text-white hover:bg-white/15 rounded-xl transition-all"
              title={t('onboarding.chatPanel.clearChat', 'Clear chat')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:text-white hover:bg-white/15 rounded-xl transition-all"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </div>
      </div>

      {/* Guided mode dismiss banner - only in assistant mode */}
      {guidedMode && onDismissGuide && mode !== 'support' && (
        <div className="px-4 py-2 bg-gradient-to-r from-blue-50 via-cyan-50 to-blue-50 dark:from-blue-900/20 dark:via-cyan-900/10 dark:to-blue-900/20 border-b border-blue-100 dark:border-blue-900/40 flex items-center justify-between shrink-0">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-cyan-300">
            <span className="text-sm">🎯</span>
            {t('onboarding.chatPanel.setupGuide', 'Setup guide')}
          </span>
          <button
            onClick={onDismissGuide}
            className="text-[11px] text-blue-600 hover:text-blue-800 dark:text-cyan-400 dark:hover:text-cyan-200 font-medium hover:underline underline-offset-2 transition-colors"
          >
            {t('onboarding.chatPanel.noGuide', "I don't need the guide")}
          </button>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4 overscroll-contain">
        {/* Welcome */}
        {showWelcome && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex gap-2.5 md:gap-3">
              <div className="relative shrink-0">
                <img src="/ai-bubble.png" alt="AI" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover ring-2 ring-blue-100 dark:ring-blue-900/40" />
              </div>
              <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl rounded-tl-md px-3.5 md:px-4 py-2.5 md:py-3 shadow-sm">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>{DOMPurify.sanitize(welcomeMessage)}</ReactMarkdown>
                </div>
              </div>
            </div>

            <div className="pl-11 md:pl-13">
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 font-semibold uppercase tracking-wider">
                {t('onboarding.chatPanel.quickQuestions', 'Quick questions')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {(quickSuggestions ?? []).slice(0, 6).map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickSuggestion(suggestion.query)}
                    disabled={isLoading}
                    style={{ animationDelay: `${index * 40}ms` }}
                    className={cn(
                      'inline-flex items-center gap-1.5 px-3 py-1.5',
                      'bg-white dark:bg-gray-900',
                      'border border-gray-200 dark:border-gray-700/60',
                      'rounded-full text-xs font-medium',
                      'text-gray-700 dark:text-gray-200',
                      'shadow-sm shadow-gray-900/5',
                      'hover:bg-gradient-to-br hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/20',
                      'hover:border-blue-300 dark:hover:border-blue-700',
                      'hover:shadow-md hover:shadow-blue-500/10 hover:-translate-y-0.5',
                      'transition-all duration-200',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'active:scale-95',
                      'animate-in fade-in slide-in-from-bottom-1'
                    )}
                  >
                    <span className="text-sm">{suggestion.icon}</span>
                    <span>{suggestion.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {visibleMessages.map((message, msgIndex) => (
          <MessageBubble
            key={message.id}
            message={message}
            isLatest={msgIndex === visibleMessages.length - 1 && message.type === 'assistant'}
            isLoading={isLoading}
            language={language}
            onExecuteAction={onExecuteAction}
            onFollowup={handleQuickSuggestion}
          />
        ))}

        {/* Loading */}
        {isLoading && (
          <div className="flex gap-2.5 md:gap-3 animate-in fade-in duration-200">
            <div className="relative shrink-0">
              <img src="/ai-bubble.png" alt="AI" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover ring-2 ring-blue-200 dark:ring-blue-800 animate-pulse" />
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl rounded-tl-md px-3.5 md:px-4 py-2.5 md:py-3 shadow-sm">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs font-medium transition-opacity duration-300">
                  {loadingMessage}
                </span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input — modernized with glow + integrated submit */}
      <form
        onSubmit={handleSubmit}
        className="relative p-3 md:p-3.5 border-t border-gray-200/60 dark:border-gray-800 bg-white dark:bg-gray-950 shrink-0 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3.5"
      >
        <div
          className={cn(
            'relative flex items-center gap-2 px-2 pr-1.5 py-1',
            'bg-gray-50 dark:bg-gray-900',
            'border border-gray-200 dark:border-gray-700/60',
            'rounded-2xl',
            'shadow-sm',
            'focus-within:border-blue-400 dark:focus-within:border-blue-600',
            'focus-within:ring-4 focus-within:ring-blue-500/10',
            'focus-within:shadow-md focus-within:shadow-blue-500/10',
            'transition-all duration-200',
            mode === 'support' && 'focus-within:border-amber-400 dark:focus-within:border-amber-600 focus-within:ring-amber-500/10 focus-within:shadow-amber-500/10'
          )}
        >
          {mode === 'support' && onUploadScreenshot && (
            <label className="cursor-pointer shrink-0">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUploadScreenshot(file);
                  e.target.value = '';
                }}
              />
              <div className="flex items-center justify-center h-8 w-8 rounded-lg text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/30 transition-colors">
                <Camera className="w-4 h-4" />
              </div>
            </label>
          )}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={mode === 'support'
              ? t('onboarding.chatPanel.supportPlaceholder', 'Describe the issue...')
              : t('onboarding.chatPanel.assistantPlaceholder', 'Ask me anything...')}
            disabled={isLoading}
            className={cn(
              'flex-1 bg-transparent border-0 outline-none',
              'text-sm placeholder:text-gray-400 dark:placeholder:text-gray-500',
              'text-gray-900 dark:text-gray-100',
              'px-1.5 py-2 min-w-0'
            )}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send"
            className={cn(
              'flex items-center justify-center h-9 w-9 rounded-xl shrink-0',
              'text-white shadow-sm',
              'transition-all duration-200',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none',
              !inputValue.trim() || isLoading
                ? 'bg-gray-300 dark:bg-gray-700'
                : mode === 'support'
                  ? 'bg-gradient-to-br from-amber-500 to-orange-500 hover:shadow-md hover:shadow-amber-500/30 hover:-translate-y-0.5 active:scale-95'
                  : 'bg-gradient-to-br from-blue-600 to-indigo-600 hover:shadow-md hover:shadow-blue-500/30 hover:-translate-y-0.5 active:scale-95'
            )}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        {/* Subtle hint below input */}
        <p className="text-[10px] text-gray-400 dark:text-gray-600 text-center mt-1.5 px-2 leading-tight">
          {mode === 'support'
            ? t('onboarding.chatPanel.aiSupportHint', 'AI may make mistakes — verify important info')
            : t('onboarding.chatPanel.aiHint', 'Powered by AI · context-aware suggestions')}
        </p>
      </form>
    </div>
  );
}

// ─── Message Bubble with Typing Animation ───

function MessageBubble({
  message,
  isLatest,
  isLoading,
  onExecuteAction,
  onFollowup,
}: {
  message: SmartAssistantMessage;
  isLatest: boolean;
  isLoading: boolean;
  language: string;
  onExecuteAction: (a: AssistantAction) => void;
  onFollowup: (q: string) => void;
}) {
  const { t } = useTranslation();
  // Only animate the latest assistant message
  const shouldAnimate = isLatest && message.type === 'assistant' && !message.isSetupCheck;
  const { displayedContent, isTyping, skipToEnd } = useTypingAnimation(
    message.content,
    shouldAnimate
  );

  const content = shouldAnimate ? displayedContent : message.content;
  const showExtras = !isTyping; // Show actions/tips/followups only after typing finishes

  return (
    <div className="space-y-2 animate-in fade-in slide-in-from-bottom-1 duration-300">
      {/* Message bubble */}
      <div
        className={cn(
          'flex gap-2.5 md:gap-3',
          message.type === 'user' ? 'flex-row-reverse' : ''
        )}
      >
        {message.type === 'user' ? (
          <div className="shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 dark:from-gray-600 dark:to-gray-800 flex items-center justify-center ring-2 ring-white dark:ring-gray-900 shadow-sm">
            <span className="text-white text-sm">👤</span>
          </div>
        ) : (
          <div className="relative shrink-0">
            <img src="/ai-bubble.png" alt="AI" className="w-9 h-9 md:w-10 md:h-10 rounded-full object-cover ring-2 ring-blue-100 dark:ring-blue-900/40 shadow-sm" />
            {isLatest && isTyping && (
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-white dark:ring-gray-950 animate-pulse" />
            )}
          </div>
        )}

        <div
          className={cn(
            'flex-1 max-w-[85%] px-3.5 md:px-4 py-2.5 md:py-3 rounded-2xl shadow-sm',
            message.type === 'user'
              ? 'bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-tr-md shadow-blue-500/20'
              : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/80 dark:to-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-tl-md'
          )}
        >
          <div
            className={cn(
              'prose prose-sm max-w-none',
              message.type === 'user' ? 'prose-invert' : 'dark:prose-invert'
            )}
          >
            <ReactMarkdown
              components={{
                a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    className={cn('underline', message.type === 'user' ? 'text-blue-200 hover:text-white' : 'text-blue-600 dark:text-cyan-400 hover:text-blue-800')}>
                    {children}
                  </a>
                ),
                p: ({ children }: { children?: React.ReactNode }) => <p className="text-sm mb-2 last:mb-0">{children}</p>,
                strong: ({ children }: { children?: React.ReactNode }) => <strong className="font-semibold">{children}</strong>,
                ul: ({ children }: { children?: React.ReactNode }) => <ul className="list-disc list-inside space-y-1 mb-2">{children}</ul>,
                ol: ({ children }: { children?: React.ReactNode }) => <ol className="list-decimal list-inside space-y-1 mb-2">{children}</ol>,
                li: ({ children }: { children?: React.ReactNode }) => <li className="text-sm">{children}</li>,
              }}
            >
              {DOMPurify.sanitize(content)}
            </ReactMarkdown>
          </div>

          {/* Skip typing button */}
          {isTyping && (
            <button
              onClick={skipToEnd}
              className="mt-1 flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <ChevronDown className="w-3 h-3" />
              {t('onboarding.chatPanel.showAll', 'Show all')}
            </button>
          )}
        </div>
      </div>

      {/* Action buttons - fade in after typing */}
      {message.type === 'assistant' && showExtras && message.actions && message.actions.length > 0 && (
        <div className={cn('pl-11 md:pl-13 flex flex-wrap gap-1.5 md:gap-2', 'animate-in fade-in slide-in-from-bottom-1 duration-300')}>
          {message.actions.map((action, index) => (
            <button
              key={index}
              onClick={() => onExecuteAction(action)}
              style={{ animationDelay: `${index * 60}ms` }}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl',
                'text-xs md:text-sm font-medium',
                'border shadow-sm',
                'hover:-translate-y-0.5 hover:shadow-md',
                'active:scale-95 transition-all duration-200',
                'animate-in fade-in',
                action.type === 'create_ticket'
                  ? 'text-amber-700 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:border-amber-300 hover:shadow-amber-500/20 dark:text-amber-300 dark:bg-amber-900/20 dark:border-amber-700/50 dark:hover:bg-amber-900/30'
                  : 'text-blue-700 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:shadow-blue-500/20 dark:text-blue-300 dark:bg-blue-900/20 dark:border-blue-700/50 dark:hover:bg-blue-900/30'
              )}
            >
              {action.label}
              <ArrowRight className="w-3 h-3 md:w-3.5 md:h-3.5" />
            </button>
          ))}
        </div>
      )}

      {/* Proactive Tips - fade in */}
      {message.type === 'assistant' && showExtras && message.proactive_tips && message.proactive_tips.length > 0 && (
        <div className={cn('pl-9 md:pl-11 space-y-1.5 md:space-y-2', 'animate-in fade-in duration-300')}>
          {message.proactive_tips.map((tip, index) => (
            <ProactiveTipCard key={index} tip={tip} onAction={onExecuteAction} />
          ))}
        </div>
      )}

      {/* Suggested Follow-ups - fade in */}
      {message.type === 'assistant' && showExtras && message.suggested_followups && message.suggested_followups.length > 0 && !isLoading && (
        <div className={cn('pl-11 md:pl-13 flex flex-wrap gap-1.5', 'animate-in fade-in slide-in-from-bottom-1 duration-300')}>
          {message.suggested_followups.map((followup, index) => (
            <button
              key={index}
              onClick={() => onFollowup(followup)}
              disabled={isLoading}
              style={{ animationDelay: `${index * 50}ms` }}
              className={cn(
                'inline-flex items-center gap-1 px-3 py-1 rounded-full',
                'text-xs font-medium',
                'bg-white dark:bg-gray-900',
                'border border-gray-200 dark:border-gray-700/60',
                'text-gray-700 dark:text-gray-200',
                'shadow-sm',
                'hover:bg-gradient-to-br hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-900/30 dark:hover:to-cyan-900/20',
                'hover:border-blue-300 dark:hover:border-blue-700',
                'hover:text-blue-700 dark:hover:text-blue-200',
                'hover:-translate-y-0.5 hover:shadow-md hover:shadow-blue-500/10',
                'transition-all duration-200',
                'active:scale-95',
                'disabled:opacity-50',
                'animate-in fade-in'
              )}
            >
              <span className="text-blue-500 dark:text-blue-400">↗</span>
              {followup}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Proactive Tip Card ───

function ProactiveTipCard({ tip, onAction }: { tip: ProactiveTip; onAction: (a: AssistantAction) => void }) {
  const isWarning = tip.severity === 'warning' || tip.type === 'missing_data';

  return (
    <div className={cn(
      'relative flex items-start gap-2.5 p-2.5 md:p-3 rounded-xl text-xs',
      'border shadow-sm overflow-hidden',
      'animate-in fade-in slide-in-from-bottom-1 duration-300',
      isWarning
        ? 'bg-gradient-to-br from-amber-50 to-orange-50/50 dark:from-amber-900/20 dark:to-orange-900/10 border-amber-200/80 dark:border-amber-700/40'
        : 'bg-gradient-to-br from-blue-50 to-cyan-50/50 dark:from-blue-900/20 dark:to-cyan-900/10 border-blue-200/80 dark:border-blue-700/40'
    )}>
      {/* Left accent bar */}
      <div className={cn(
        'absolute left-0 top-0 bottom-0 w-1 rounded-l-xl',
        isWarning ? 'bg-amber-400' : 'bg-blue-500'
      )} />
      <div className={cn(
        'shrink-0 mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center',
        isWarning
          ? 'bg-amber-100 dark:bg-amber-900/40'
          : 'bg-blue-100 dark:bg-blue-900/40'
      )}>
        {isWarning ? (
          <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        ) : (
          <Lightbulb className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
        )}
      </div>
      <div className="flex-1 min-w-0 pl-0.5">
        <p className={cn('leading-relaxed font-medium', isWarning ? 'text-amber-900 dark:text-amber-100' : 'text-blue-900 dark:text-blue-100')}>
          {tip.message}
        </p>
        {tip.action && (
          <button
            onClick={() => onAction(tip.action!)}
            className={cn(
              'mt-1.5 inline-flex items-center gap-1 font-semibold text-[11px]',
              'px-2 py-1 rounded-md',
              'active:scale-95 transition-all duration-200',
              'hover:-translate-y-0.5',
              isWarning
                ? 'text-amber-700 dark:text-amber-300 bg-amber-100/70 hover:bg-amber-200/80 dark:bg-amber-800/30 dark:hover:bg-amber-800/50'
                : 'text-blue-700 dark:text-blue-300 bg-blue-100/70 hover:bg-blue-200/80 dark:bg-blue-800/30 dark:hover:bg-blue-800/50'
            )}
          >
            {tip.action.label}
            <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

export default OnboardingChatPanel;
